import React, { useEffect, useState, useCallback, useRef } from 'react';
import Carousel from 'react-bootstrap/Carousel';
import '../style/Login.css';
import { LuPencilLine } from 'react-icons/lu';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthService, fetchWithAuth } from './authService';
import { API_ENDPOINTS } from '../utils/constants';
import { useStatsigClient, useStatsigUser } from '@statsig/react-bindings';

// Function to check if Web OTP API is supported
const isWebOTPSupported = () => {
  return 'OTPCredential' in window;
};

const isSafari = () => {
  const userAgent = navigator.userAgent;
  return (
    /^((?!chrome|android).)*safari/i.test(userAgent) ||
    /iPad|iPhone|iPod/.test(userAgent)
  );
};

const Login = () => {
  const [isLoading, setIsLoading] = useState(false); // Loader state
  const [toOtp, settoOtp] = useState(false);
  const [toName, settoName] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otp, setotp] = useState('');
  const [name, setName] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [code, setCode] = useState('+91');
  const [token, setToken] = useState(null);
  const [config, setConfig] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [authenticationCompleted, setAuthenticationCompleted] = useState(false);
  const [otpId, setOtpId] = useState(null); // Track OTP session
  const location = useLocation();
  const fromPathRef = useRef(location.state?.from || '/main');
  const navigate = useNavigate();
  const authCallCountRef = useRef(0);
  const processingRef = useRef(false);
  const authCompletedRef = useRef(false);
  const otpInputRef = useRef(null);
  const { client } = useStatsigClient();
  const { updateUserAsync } = useStatsigUser();
  // Enhanced iOS detection
  const isIOSDevice = () => {
    return (
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.userAgent.includes('Safari') &&
        !navigator.userAgent.includes('Chrome'))
    );
  };

  // Enhanced Android detection
  const isAndroidDevice = () => {
    return /Android/.test(navigator.userAgent);
  };

  useEffect(() => {
    const hasReloaded = sessionStorage.getItem('hasReloaded');

    if (!hasReloaded) {
      // First load — App mount, not a refresh
      console.log('App loaded via navigation');
      if (client) {
        if (localStorage.getItem('isQrEntry') == 'true') {
          client.logEvent('appOpen', 'opened', { source: 'qrScan' });
        } else {
          client.logEvent('appOpen', 'opened', { source: 'null' });
        }
      }
      sessionStorage.setItem('hasReloaded', 'true');
    } else {
      // This is a page refresh (hard reload)
      console.log('Page was refreshed');
    }
  }, [client]);
  // Add focus management for better iOS Safari AutoFill
  useEffect(() => {
    if (toOtp && otpInputRef.current) {
      // Small delay to ensure smooth transition and better AutoFill detection
      const focusTimer = setTimeout(() => {
        otpInputRef.current.focus();
      }, 500);

      return () => clearTimeout(focusTimer);
    }
  }, [toOtp]);

  useEffect(() => {
    const token = AuthService.getAccessToken();
    if (token && !AuthService.isTokenExpired()) {
      navigate('/main'); // ✅ Redirect if valid token exists
    }
    // Reset processing state on mount
    setIsProcessing(false);
    setAuthenticationCompleted(false);
    authCallCountRef.current = 0;
    processingRef.current = false;
    authCompletedRef.current = false;
  }, [navigate]);

  // ✅ Add debugging function
  const debugTokenState = location => {
    const token = localStorage.getItem('tringAuthToken');
    console.log(`🔍 Token check at ${location}:`, {
      exists: !!token,
      length: token?.length || 0,
      preview: token?.substring(0, 20) + '...' || 'none',
    });
  };

  // ✅ Helper function to ensure token exists before navigation
  const ensureTokenAndNavigate = useCallback(
    redirectPath => {
      const token = localStorage.getItem('tringAuthToken');
      if (!token) {
        console.error('❌ Token missing before navigation');
        // Reset state and stay on login
        setIsProcessing(false);
        setAuthenticationCompleted(false);
        processingRef.current = false;
        authCompletedRef.current = false;
        return;
      }

      console.log(
        '✅ Token confirmed before navigation:',
        token.substring(0, 20) + '...'
      );
      navigate(redirectPath);
    },
    [navigate]
  );

  // Function to fetch user configuration after successful authentication
  const fetchConfig = async () => {
    try {
      const response = await fetchWithAuth(API_ENDPOINTS.USER_CONFIG, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) throw new Error('Failed to fetch data');

      const data = await response.json();
      setConfig(data);
      try {
        localStorage.setItem('shoutoutBanner', data?.coinBanner);
        localStorage.setItem('ChancePoster', data?.chancePoster);
        if (isSafari()) {
          sessionStorage.setItem('shoutoutBanner', data?.coinBanner);
        }
      } catch (error) {
        console.warn('Failed to store config:', error);
      }
    } catch (error) {
      console.error('Error fetching config:', error);
      // Don't throw - let the authentication flow continue
    }
  };

  // ✅ Updated registerUser with better token handling
  const registerUser = useCallback(
    async (accessToken, phoneNo, redirectPath = '/main') => {
      try {
        // ✅ Set token using AuthService
        AuthService.setAccessToken(accessToken);

        const response = await fetchWithAuth(API_ENDPOINTS.USERS, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            // Header will be added by fetchWithAuth
          },
          body: JSON.stringify({
            phone_number: code + phoneNo,
          }),
        });

        if (!response.ok) {
          throw new Error('User registration failed');
        }

        const data = await response.json();
        console.log('Registration successful', data);

        // ✅ Double-check token is still there before navigating
        localStorage.setItem('tringAuthToken', accessToken);
        // Ensure local caches for Statsig defaults are updated
        if (data?.id) localStorage.setItem('userId', data.id);
        if (data?.first_name) localStorage.setItem('name', data.first_name);

        try {
          await updateUserAsync({
            userID: data?.id || localStorage.getItem('userId'),
            custom: {
              name: data?.first_name || localStorage.getItem('name'),
            },
          });
        } catch (e) {
          console.warn('Statsig user update (register) failed:', e);
        }
        debugTokenState('registerUser before navigation');
        ensureTokenAndNavigate(redirectPath);
      } catch (error) {
        console.error('Registration error:', error);
        // ✅ Clean up on error
        localStorage.removeItem('tringAuthToken');
        localStorage.removeItem('userId');
        AuthService.clearTokens();
        setToken(null);
        alert('Registration failed. Please try again.');

        // Reset processing state
        setIsProcessing(false);
        setAuthenticationCompleted(false);
        processingRef.current = false;
        authCompletedRef.current = false;
      }
    },
    [code, ensureTokenAndNavigate, updateUserAsync]
  );

  // ✅ Updated handleAuthentication to work with verify OTP response
  const handleAuthentication = useCallback(
    async verifyResponse => {
      authCallCountRef.current += 1;
      console.log(`🔄 Authentication attempt #${authCallCountRef.current}`);

      if (processingRef.current || authCompletedRef.current) {
        console.log('🛑 Authentication already in progress or completed');
        return;
      }

      const existingToken = AuthService.getAccessToken();
      if (existingToken && !AuthService.isTokenExpired()) {
        console.log('✅ Valid token exists, navigating to main');
        navigate('/main');
        return;
      }

      processingRef.current = true;
      setIsProcessing(true);

      try {
        console.log('🚀 Starting authentication process...');
        console.log('Verify OTP response:', verifyResponse);

        // ✅ Store token IMMEDIATELY after getting verify response
        AuthService.setAccessToken(
          verifyResponse.tokens.accessToken,
          verifyResponse.tokens.expiresIn
        );
        if (verifyResponse.tokens?.refreshToken) {
          AuthService.setRefreshToken(verifyResponse.tokens.refreshToken);
        }

        localStorage.setItem(
          'tringAuthToken',
          verifyResponse.tokens.accessToken
        );
        localStorage.setItem('userId', verifyResponse.id);
        localStorage.setItem('name', verifyResponse.first_name);
        setToken(verifyResponse.tokens.accessToken);

        debugTokenState('after verify OTP');

        // Update Statsig user as early as possible
        try {
          const statsigUserId =
            verifyResponse?.id || localStorage.getItem('userId');
          const statsigName =
            verifyResponse?.first_name || localStorage.getItem('name');
          if (statsigUserId) {
            await updateUserAsync({
              userID: statsigUserId,
              custom: { name: statsigName },
            });
          }
        } catch (e) {
          console.warn('Statsig user update (verify) failed:', e);
        }

        const phoneNo = localStorage.getItem('number');
        const redirectPath = fromPathRef.current;

        // ✅ Fetch config but don't let it block the flow
        try {
          await fetchConfig();
        } catch (configError) {
          console.warn(
            'Config fetch failed, but continuing with authentication:',
            configError
          );
        }

        authCompletedRef.current = true;
        setAuthenticationCompleted(true);

        if (verifyResponse.isUserRegistered) {
          console.log('✅ User is registered, navigating...');
          debugTokenState('before registered user navigation');
          ensureTokenAndNavigate(redirectPath);
        } else {
          console.log('🔄 User not registered - initiating registration');
          await registerUser(
            verifyResponse.tokens.accessToken,
            phoneNo,
            redirectPath
          );
        }
      } catch (error) {
        console.error('❌ Authentication failed:', error);

        // ✅ Clean up token and state on error
        localStorage.removeItem('tringAuthToken');
        localStorage.removeItem('userId');
        setToken(null);
        alert('Verification failed');

        processingRef.current = false;
        authCompletedRef.current = false;
        setIsProcessing(false);
        setAuthenticationCompleted(false);
      }
    },
    [navigate, registerUser, ensureTokenAndNavigate, updateUserAsync]
  );

  useEffect(() => {
    // Simple initialization - no OTPless script loading needed
    setIsLoading(false);

    return () => {
      console.log('🧹 Cleaning up Login component');
      setIsProcessing(false); // Reset processing state
      setAuthenticationCompleted(false); // Reset completion state
      authCallCountRef.current = 0; // Reset call counter
      processingRef.current = false; // Reset processing ref
      authCompletedRef.current = false; // Reset completion ref
    };
  }, []);

  const handleNumchange = e => {
    const value = e.target.value;
    setPhoneNumber(value);
  };

  const sendOTP = useCallback(async () => {
    if (phoneNumber.length !== 10) {
      alert('Please enter a valid 10-digit phone number.');
      return;
    }

    setIsProcessing(true);
    setAuthenticationCompleted(false);
    authCallCountRef.current = 0;
    processingRef.current = false;
    authCompletedRef.current = false;

    localStorage.setItem('number', phoneNumber);

    try {
      // Format phone number: remove + from country code and combine
      const formattedPhoneNumber = code.replace('+', '') + phoneNumber;

      const response = await fetch(API_ENDPOINTS.SEND_OTP, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phoneNumber: formattedPhoneNumber,
          appId: 'user',
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to send OTP');
      }

      const data = await response.json();
      console.log('OTP sent successfully:', data);

      if (data.success) {
        setOtpId(data.otpId);
        settoOtp(true);
        setCountdown(30);
      } else {
        throw new Error('OTP send failed');
      }
    } catch (error) {
      console.error('❌ Error sending OTP:', error);
      alert('Failed to send OTP. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  }, [phoneNumber, code]);

  useEffect(() => {
    if (phoneNumber.length === 10) {
      sendOTP();
      localStorage.setItem('number', phoneNumber);
    }
  }, [phoneNumber, sendOTP]);

  const handleChange = () => {
    settoOtp(false);
    setotp('');
    setCountdown(0);
  };

  const handleOTPchange = e => {
    const value = e.target.value;

    // Only allow numeric input and limit to 6 digits
    const numericValue = value.replace(/\D/g, '').slice(0, 6);
    setotp(numericValue);
  };

  const handleNamechange = e => {
    const value = e.target.value;
    setName(value);
  };

  const verifyOTP = useCallback(async () => {
    if (otp.length !== 6) {
      alert('Please enter a valid OTP.');
      return;
    }

    if (!otpId) {
      console.error('❌ No OTP session found');
      alert('Please request OTP again.');
      return;
    }

    setIsProcessing(true);

    try {
      // Format phone number: remove + from country code and combine
      const formattedPhoneNumber = code.replace('+', '') + phoneNumber;

      const response = await fetch(API_ENDPOINTS.VERIFY_OTP, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phoneNumber: formattedPhoneNumber,
          otpCode: otp,
          appId: 'user',
          platform: 'browser',
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to verify OTP');
      }

      const data = await response.json();
      console.log('OTP verified successfully:', data);

      if (data.success) {
        // The verify OTP response contains all the session data
        await handleAuthentication(data);
      } else {
        throw new Error('OTP verification failed');
      }
    } catch (error) {
      console.error('❌ Error verifying OTP:', error);
      alert('Invalid OTP. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  }, [otp, otpId, phoneNumber, code, handleAuthentication]);

  const resendOTP = useCallback(async () => {
    if (countdown > 0 || isProcessing) {
      return;
    }

    setIsProcessing(true);

    try {
      // Format phone number: remove + from country code and combine
      const formattedPhoneNumber = code.replace('+', '') + phoneNumber;

      const response = await fetch(API_ENDPOINTS.RESEND_OTP, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phoneNumber: formattedPhoneNumber,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to resend OTP');
      }

      const data = await response.json();
      console.log('OTP resent successfully:', data);

      if (data.success) {
        setOtpId(data.otpId);
        setCountdown(30);
        setotp(''); // Clear the OTP input
      } else {
        throw new Error('OTP resend failed');
      }
    } catch (error) {
      console.error('❌ Error resending OTP:', error);
      alert('Failed to resend OTP. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  }, [countdown, isProcessing, phoneNumber, code]);

  useEffect(() => {
    if (otp.length === 6 && /^\d{6}$/.test(otp)) {
      console.log('🔐 Auto-verifying OTP:', otp);
      // Small delay to ensure user sees the filled OTP
      const verifyTimer = setTimeout(() => {
        verifyOTP();
      }, 500);

      return () => clearTimeout(verifyTimer);
    }
  }, [otp, verifyOTP]);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // Web OTP API support
  // Enhanced Web OTP API support (fallback)
  useEffect(() => {
    if (!toOtp || !isWebOTPSupported()) return;

    const abortController = new AbortController();

    const handleOTPReceive = async () => {
      try {
        console.log('🔄 Attempting Web OTP API...');

        const credential = await navigator.credentials.get({
          otp: { transport: ['sms'] },
          signal: abortController.signal,
        });

        if (credential && credential.code) {
          console.log('✅ Web OTP API received:', credential.code);

          // Validate the received OTP
          const otpCode = credential.code.toString();
          if (otpCode.length === 6 && /^\d{6}$/.test(otpCode)) {
            setotp(otpCode);
          } else {
            console.log('⚠️ Invalid OTP from Web API:', otpCode);
          }
        }
      } catch (error) {
        if (error.name === 'AbortError') {
          console.log('🛑 Web OTP request aborted');
        } else if (error.name === 'NotSupportedError') {
          console.log(
            '📱 Web OTP API not supported (this is normal on many devices)'
          );
        } else if (error.name === 'SecurityError') {
          console.log('🔒 Web OTP API security error - ensure HTTPS');
        } else {
          console.log(
            '⚠️ Web OTP API error (fallback to manual entry):',
            error.message
          );
        }
        // Don't show error to user - this is expected on many devices
      }
    };

    // Delay the Web OTP API call to allow other methods to work first
    const webOTPTimer = setTimeout(() => {
      handleOTPReceive();
    }, 1500);

    return () => {
      clearTimeout(webOTPTimer);
      abortController.abort();
    };
  }, [toOtp]);

  function changeCode(code) {
    setCode(code);
  }

  return (
    <div
      style={{ height: '100dvh', overflow: 'scroll', scrollBehavior: 'smooth' }}
    >
      {!toOtp ? (
        <div className="login-container h-100 w-100">
          <Carousel
            controls={false}
            className="login-corousel"
            style={{ height: '70%' }}
          >
            <Carousel.Item interval={2000} style={{ height: '90%' }}>
              <img
                className="d-flex w-100 h-100"
                src="/images/onboard1.png"
                alt="First slide"
              />
            </Carousel.Item>

            <Carousel.Item interval={2000} style={{ height: '90%' }}>
              <img
                className="d-flex w-100 h-100"
                src="/images/onboard2.png"
                alt="Second slide"
              />
            </Carousel.Item>

            <Carousel.Item interval={2000} style={{ height: '90%' }}>
              <img
                className="d-flex w-100 h-100"
                src="/images/onboard3.png"
                alt="Third slide"
              />
            </Carousel.Item>
          </Carousel>

          <div
            className="d-flex flex-column align-items-center justify-content-start"
            style={{ minHeight: '30%', flexGrow: 1 }}
          >
            <span className="common-txt mb-3" style={{ fontWeight: 600 }}>
              Just sign-in with your phone number
            </span>
            <div
              className="login-input-container d-flex flex-row mb-3"
              style={{ padding: '12px' }}
            >
              <div
                className="d-flex flex-row justify-content-center align-items-center"
                style={{ marginRight: '5px' }}
              >
                <select
                  style={{
                    border: 'none',
                    background: 'white',
                    outline: 'none',
                  }}
                  className="mx-1"
                  onChange={e => changeCode(e.target.value)}
                  value={code}
                >
                  <option value="+91">+91</option>
                  <option value="+1">+1</option>
                </select>
              </div>

              <input
                type="number"
                style={{
                  border: 'none',
                  fontSize: '16px',
                  color: '#222', // applies only to entered text, not placeholder
                  background: 'white',
                  fontWeight: '550',
                }}
                className="login-input"
                placeholder="Enter 10 digit number"
                value={phoneNumber}
                onChange={e => {
                  const value = e.target.value;
                  if (/^\d{0,10}$/.test(value)) {
                    handleNumchange(e);
                  }
                }}
                onKeyDown={e => {
                  if (['e', 'E', '.', '-', '+'].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                onWheel={e => e.target.blur()}
                disabled={isProcessing}
              />
            </div>
            <button
              onClick={() => sendOTP()}
              className="send-btn p-2 mb-4"
              disabled={isProcessing || phoneNumber.length !== 10}
            >
              Send OTP
            </button>

            <div className="d-flex flex-column align-items-center justify-content-center">
              <span className="" style={{ color: 'rgba(76, 76, 76, 0.50)' }}>
                By registering, you agree to our
              </span>
              <span
                className=""
                onClick={() => window.open(config?.privacyPolicyLink, '_blank')}
                style={{ cursor: 'pointer' }}
              >
                <span style={{ color: ' #D51217' }}>Terms of Use </span>{' '}
                <span style={{ color: ' rgba(76, 76, 76, 0.50)' }}>&</span>{' '}
                <span style={{ color: ' #D51217' }}>Privacy Policy</span>
              </span>
            </div>
          </div>
        </div>
      ) : (
        <div className="">
          {!toName ? (
            <div
              className="otp-page-container d-flex flex-column align-items-center"
              style={{ height: '100dvh' }}
            >
              <div
                className="w-100"
                style={{
                  position: 'relative',
                  height: '70%',
                  backgroundImage: 'url(/images/onboard42.png)',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'none',
                  backgroundSize: '100% 100%',
                }}
              >
                <img
                  style={{
                    position: 'absolute',
                    bottom: '-20px',
                    right: '-20px',
                    height: '35%',
                  }}
                  src="/images/timer.gif"
                />
              </div>
              <div className="d-flex flex-column" style={{ width: '80%' }}>
                <div className="d-flex flex-row align-items-center justify-content-between mt-3 mb-3">
                  <span
                    className=" common-txt"
                    style={{
                      fontSize: 'clamp(0px,4.4vw,18px)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    OTP sent on:{' '}
                    <b>
                      {code}
                      <span>{phoneNumber}</span>
                    </b>
                  </span>
                  <button
                    onClick={() => handleChange()}
                    className="common-txt"
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                    }}
                    disabled={isProcessing}
                  >
                    <LuPencilLine
                      style={{ fontSize: 'clamp(0px,4.4vw,18px)' }}
                      color="black"
                    />
                  </button>
                </div>

                <div className="d-flex flex-column">
                  <input
                    ref={otpInputRef}
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    autoFocus={true}
                    id="otp-input"
                    name="otp"
                    className="otp-input p-2 mb-2"
                    value={otp}
                    onChange={handleOTPchange}
                    maxLength={6}
                    placeholder="Enter 6-digit OTP"
                    pattern="\d{6}"
                    disabled={isProcessing}
                    onKeyDown={e => {
                      if (['e', 'E', '.', '-', '+'].includes(e.key)) {
                        e.preventDefault();
                      }
                    }}
                  />
                  <button
                    onClick={() => verifyOTP()}
                    className="otp-verify-btn p-2 mb-1"
                    disabled={isProcessing || otp.length !== 6}
                  >
                    Verify OTP
                  </button>
                </div>

                <div className="d-flex flex-row justify-content-between align-items-center mb-3 mt-1">
                  <span
                    className="common-txt"
                    style={{ color: 'rgba(76, 76, 76, 0.50)' }}
                  >
                    0:{countdown.toString().padStart(2, '0')}
                  </span>
                  <button
                    className="common-txt"
                    style={{
                      border: '',
                      background: 'white',
                      borderRadius: '10px',
                      color:
                        countdown > 0 || isProcessing
                          ? 'rgba(76, 76, 76, 0.50)'
                          : 'black',
                      cursor:
                        countdown > 0 || isProcessing
                          ? 'not-allowed'
                          : 'pointer',
                    }}
                    disabled={countdown > 0 || isProcessing}
                    onClick={() => resendOTP()}
                  >
                    Resend OTP
                  </button>
                </div>

                <div className="d-flex flex-column align-items-center justify-content-center">
                  <span
                    className=""
                    style={{ color: 'rgba(76, 76, 76, 0.50)' }}
                  >
                    By registering, you agree to our
                  </span>
                  <span
                    className=""
                    onClick={() =>
                      window.open(config?.privacyPolicyLink, '_blank')
                    }
                    style={{ cursor: 'pointer' }}
                  >
                    <span style={{ color: ' #D51217' }}>Terms of Use </span>{' '}
                    <span style={{ color: ' rgba(76, 76, 76, 0.50)' }}>&</span>{' '}
                    <span style={{ color: ' #D51217' }}>Privacy Policy</span>
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <div></div>
          )}
        </div>
      )}

      {toName ? (
        <div
          className="d-flex flex-column align-items-center"
          style={{ height: '100dvh' }}
        >
          <div
            className="w-100"
            style={{
              position: 'relative',
              height: '70%',
              backgroundImage: 'url(/images/onboard42.png)',
              backgroundPosition: 'center',
              backgroundRepeat: 'none',
              backgroundSize: '100% 100%',
            }}
          >
            <img
              style={{
                position: 'absolute',
                bottom: '-20px',
                right: '-20px',
                height: '35%',
              }}
              src="/images/timer.gif"
            />
          </div>
          <div className="d-flex flex-column" style={{ width: '80%' }}>
            <div className="d-flex flex-row align-items-center justify-content-between mt-3 mb-2">
              <span
                className=" common-txt"
                style={{
                  fontSize: 'clamp(0px,4.4vw,18px)',
                  whiteSpace: 'nowrap',
                }}
              >
                Please Enter Your Name:
              </span>
            </div>

            <div className="d-flex flex-column">
              <input
                type="text"
                className="otp-input p-2 mb-2"
                value={name}
                onChange={handleNamechange}
                placeholder="Enter your name"
                disabled={isProcessing}
              />
              <button
                className="otp-verify-btn p-2 mb-1"
                disabled={isProcessing || !name.trim()}
                // onClick={() => handleContinue()}
              >
                {isProcessing ? 'Processing...' : 'Continue'}
              </button>
            </div>

            <div className="d-flex flex-column align-items-center justify-content-center mt-2">
              <span className="" style={{ color: 'rgba(76, 76, 76, 0.50)' }}>
                By registering, you agree to our
              </span>
              <span
                className=""
                style={{ cursor: 'pointer' }}
                onClick={() => window.open(config?.privacyPolicyLink, '_blank')}
              >
                <span style={{ color: ' #D51217' }}>Terms of Use </span>{' '}
                <span style={{ color: ' rgba(76, 76, 76, 0.50)' }}>&</span>{' '}
                <span style={{ color: ' #D51217' }}>Privacy Policy</span>
              </span>
            </div>
          </div>
        </div>
      ) : (
        <div></div>
      )}
    </div>
  );
};

export default Login;
